// One request waiting for confirmation, as a card (03-the-guardrails-
// service.md § the console): what was asked, the snapshot fields the rule
// named, who asked, when, by which rule; *Approve*, confirmed with its
// consequence, and *Deny*; after approving, the card says to go back and
// ask again. On the request's own page the rule's expression follows what
// was asked, as its first line with *Show more*, and the identifiers are
// quiet and shortened with copy (01-the-console.md § conventions: density
// and length). Where a call stopped — a declared element's, a chat turn —
// the same card is drawn there, saying when the request runs out, with
// *Approve and send* where the element asks again itself (14-solutions/
// 03-the-console.md § the words; hq decision 0083).

import { useState } from "react";
import { Link } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { Confirm, NotAllowed, Notice, Pill, since, useLoad, when } from "../../ui/components.tsx";
import { Ident } from "../../ui/ident.tsx";
import { MoreText } from "../../ui/more.tsx";
import { isRefusal } from "../../platform/bus.ts";
import {
  Approver,
  Decision,
  Code,
  addressOf,
  requiredSubject,
  ruleRef,
  ruleSubject,
  SERVICE,
  Status,
  type Decision as DecisionT,
  type Record_,
  type RuleEntry,
} from "../../wire/guard.ts";
import { useSurface } from "../surface.tsx";
import { resolveUnder } from "./resolve.ts";
import {
  APPROVE_AND_SEND_CONSEQUENCE,
  APPROVE_CONSEQUENCE,
  contextRows,
  DENY_CONSEQUENCE,
  describeSubject,
  principalWords,
  statusWords,
} from "./words.ts";

export function RequestCard({
  record,
  full,
  onResolved,
  resend,
  quiet,
}: {
  record: Record_;
  /** Whether this is the request's own page: every field, the id in monospace. */
  full?: boolean;
  onResolved?: (decision: DecisionT) => void;
  /** Whether approving also asks again, as a chat's turn does: *Approve and send*. */
  resend?: boolean;
  /** Whether the card leaves saying what follows a decision to where it is drawn. */
  quiet?: boolean;
}) {
  const s = useSurface();
  const [decided, setDecided] = useState<DecisionT | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);
  const [busy, setBusy] = useState(false);
  const open = record.status === Status.open && decided === undefined;
  const status =
    decided === undefined ? record.status : decided === Decision.granted ? Status.granted : Status.denied;
  const sw = statusWords(status);
  const rows = contextRows(record.context);
  const who = principalWords(record.principal, s.principal);
  const ref = ruleRef(record.rule, record.revision);
  const forAdmins = record.approver === Approver.admin;

  const resolve = (decision: DecisionT) => {
    setBusy(true);
    setErr(undefined);
    resolveUnder(s.bus, addressOf(record), record.id, decision).then(
      () => {
        setBusy(false);
        setDecided(decision);
        onResolved?.(decision);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };

  return (
    <div className="card stack">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="grow">
          <h3>
            {full ? (
              describeSubject(record.subject)
            ) : (
              <Link className="text-link" to={tenantPath(s.tenant, "approvals", record.id)}>
                {describeSubject(record.subject)}
              </Link>
            )}
          </h3>
          <p className="small muted" style={{ marginTop: 4 }}>
            {who === "you" ? "You asked" : `${who} asked`} {since(record.opened) || when(record.opened)}
            {forAdmins ? ", and an admin decides" : ""}
            {!full && open ? `; it waits until ${when(record.expires)}` : ""}; the guardrail{" "}
            {s.offered("guardrails") ? (
              <Link className="text-link" to={tenantPath(s.tenant, "guardrails", record.rule)}>
                {record.rule}
              </Link>
            ) : (
              record.rule
            )}{" "}
            needs confirmation.
          </p>
        </div>
        <Pill tone={sw.tone}>{sw.word}</Pill>
      </div>
      {rows.length > 0 && (
        <dl className="kv small">
          {rows.map((r) => (
            <RowPair key={r.key} row={r} />
          ))}
        </dl>
      )}
      {full && <TheRule id={record.rule} />}
      {full && (
        <dl className="kv small">
          <dt>Asked on</dt>
          <dd>
            <Ident value={record.subject} label="the subject" head={32} tail={8} />
          </dd>
          <dt>Guardrail</dt>
          <dd className="mono">{ref}</dd>
          {record.caller !== undefined && (
            <>
              <dt>Through</dt>
              <dd>
                <Ident value={record.caller.workload} label="the workload's id" />
              </dd>
            </>
          )}
          <dt>Opened</dt>
          <dd>{when(record.opened)}</dd>
          <dt>{open ? "Waits until" : "Waited until"}</dt>
          <dd>{when(record.expires)}</dd>
          {record.resolution !== undefined && (
            <>
              <dt>{record.resolution.decision === Decision.granted ? "Approved" : "Denied"}</dt>
              <dd>
                by {principalWords(record.resolution.resolver, s.principal)}, {when(record.resolution.at)}
                {record.resolution.grant_expires !== undefined && (
                  <span className="muted"> · good until {when(record.resolution.grant_expires)}</span>
                )}
              </dd>
            </>
          )}
          <dt>Id</dt>
          <dd>
            <Ident value={record.id} label="the request's id" />
          </dd>
        </dl>
      )}
      {err !== undefined && <NotAllowed err={err} />}
      {!quiet && decided === Decision.granted && (
        <Notice kind="ok" title="Approved">
          Go back to where you asked and ask again, exactly as before; it goes through once, until{" "}
          {record["grant-ttl"]} from now.
        </Notice>
      )}
      {!quiet && decided === Decision.denied && (
        <Notice title="Denied">Whoever was waiting is told to stop.</Notice>
      )}
      {open && (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Confirm
            kind="default"
            label={resend ? "Approve and send" : "Approve"}
            consequence={resend ? APPROVE_AND_SEND_CONSEQUENCE : APPROVE_CONSEQUENCE}
            disabled={busy}
            onConfirm={() => {
              resolve(Decision.granted);
            }}
          />
          <Confirm
            label="Deny"
            consequence={DENY_CONSEQUENCE}
            disabled={busy}
            onConfirm={() => {
              resolve(Decision.denied);
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * TheRule is the guardrail's expression as it reads now, its first line with
 * *Show more*: read where the role may read guardrails — the workspace's own
 * rule, else a required one — and left out where it may not.
 */
function TheRule({ id }: { id: string }) {
  const s = useSurface();
  const reads = s.offered("guardrails");
  const [rule] = useLoad(`approvals/rule/${id}/${reads ? "read" : "none"}`, () =>
    reads
      ? s.bus.call<RuleEntry>(SERVICE, ruleSubject("get", id), undefined).catch((err: unknown) => {
          if (isRefusal(err, Code.notFound)) {
            return s.bus.call<RuleEntry>(SERVICE, requiredSubject("get", id), undefined);
          }
          throw err;
        })
      : Promise.resolve(undefined),
  );
  if (rule.state !== "ready" || rule.value === undefined) {
    return null;
  }
  return (
    <div className="field">
      <span className="label">The rule</span>
      <MoreText text={rule.value.rule.expression} />
    </div>
  );
}

function RowPair({ row }: { row: { key: string; value: string } }) {
  return (
    <>
      <dt className="mono">{row.key}</dt>
      <dd className="mono">{row.value}</dd>
    </>
  );
}
